/* eslint-disable react/jsx-key */
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { translate } from 'components/Translation';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import LocationTab from 'views/AddNewServices/AddSingleService/tabs/LocationTab';
import { Card, makeStyles } from '@material-ui/core';
import { Form } from './Form/Form';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/MicroBundle/addNewMicroBundleStyle';
import { MBPlans } from './Plans/MBPlans';
import { useSelector } from 'react-redux';
import { userData } from 'redux/modules/session/selectors';
import { useFormik, FormikValues, FormikProps } from 'formik';
import { NavButtons } from './commons/NavButtons';
import { SubmitSalesLeads } from 'services/submitSalesLeads';
import StatusDialog from '../../../components/StatusDialog/StatusDialog';
import * as Yup from 'yup';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import { AdditionalBenefits } from './AdditionalBenefits/AdditionalBenefits';
import { selectedAccountSelector } from 'redux/modules/addServices/selectors';
import { getTemplateKeys } from 'redux/modules/dynamicLabels/selectors';
import Button from 'components/CustomButtons/Button';
import { FAILURE, ROUTES, SUCCESS, TAB_INDEXES } from './constants';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import { getLanguage } from 'utils/language/language';
import { TagManager } from 'commons/gtm/tagManager';
import { loadQualtrics } from 'utils/qualtrics';

interface IProps extends RouteComponentProps {}

// @ts-ignore
const useStyles = makeStyles(styles);

export interface ICustomFormikProps extends Omit<FormikProps<FormikValues>, 'setFormikState'> {}
interface IAddNewMbProps {
	account?: IAlternativeSelectItem | null;
}

const AddNewMb = (props: IAddNewMbProps) => {
	//Contact Details Form Schema
	const FormSchema = Yup.object().shape({
		contactNumber: Yup.string()
			.min(8, translate('mb.contact.validation'))
			.max(8, translate('mb.contact.validation'))
			.required(translate('mb.contact.validation')),
		email: Yup.string()
			.required(translate('mb.email.validation'))
			.max(80, translate('mb.email.validation'))
			.matches(/^[A-Za-z0-9._%'-+]+@[A-Za-z0-9.-]+\.[a-zA-Z]{2,62}$/, translate('mb.email.validation')),
		name: Yup.string()
			.required(translate('mb.name.validation'))
			.min(1, translate('mb.name.validation'))
			.max(80, translate('mb.name.validation'))
			.test('not-blank', translate('mb.name.validation'), (value) => {
				return value?.trim() !== '';
			}),
	});
	const classes = useStyles();
	const [nextStepDisabled, setNextStepDisabled] = useState<boolean>(true);
	const [gis, setGis] = useState<string>('');
	const [address, setAddress] = useState<{ [key: string]: string }>({});
	const [location, setLocation] = useState<{ [key: string]: string }>({});
	const [index, setIndex] = useState(0);
	// selection plan on the Plans page, these values are used in the contact detais from component and alse used in API request payload
	const [selectedOption, setSelectedOption] = useState<types.ecommerce.SelectedPlan>({
		name: '',
		uniqueID: '',
		price: '',
		partNumber: '',
		displayName: '',
		discount: '',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [success, setSuccess] = useState(false);
	const [refNumber, setRefNumber] = useState<string>('');
	const [additionalServices, setAdditionalServices] = useState([]);
	const [checkedState, setCheckedState] = useState<boolean[]>(new Array().fill(false));
	const [productInfo, setProductInfo] = useState<any>([]);
	const { state }: any = useLocation();

	// const showNavButtons = index === TAB_INDEXES.BENEFITS;
	const showNavButtons = false;
	const history = useHistory();
	const selectedAccount = useSelector(selectedAccountSelector);
	const templateKeys = useSelector(getTemplateKeys);
	const routeToOrigin = () => {
		let origin = state?.origin;
		if (!origin) origin = ROUTES.SERVICES;
		return history.push(origin);
	};
	const openModal = () => {
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		if (success) {
			routeToOrigin();
		}
		loadQualtrics({ activity_type: 'Add New Service - Aamali' });
	};
	const userDetails = useSelector(userData);

	const formik = useFormik({
		initialValues: {
			email: userDetails?.email || '',
			contactNumber: typeof userDetails?.contactNumber !== 'string' ? '' : userDetails?.contactNumber,
			name: userDetails?.userName || '',
			plan: '',
		},
		validateOnBlur: true,
		validateOnChange: true,
		onSubmit: (values: FormikValues) => {},
		validationSchema: FormSchema,
	});

	const componentRef: RefObject<HTMLDivElement> = useRef(null);
	const navButtonsRef: RefObject<HTMLDivElement> = useRef(null);

	useEffect(() => {
		if (componentRef.current) {
			componentRef?.current.focus();
		}
	}, [index == TAB_INDEXES.LOCATION]);

	useEffect(() => {
		if (index === TAB_INDEXES.FORM) {
			formik.validateForm();
		}
	}, [getLanguage()]);

	useEffect(() => {
		if (index === TAB_INDEXES.LOCATION && !nextStepDisabled) {
			if (navButtonsRef.current) {
				window.scrollTo({
					top: navButtonsRef.current.offsetTop,
					behavior: 'smooth',
				});
			}
		}
	}, [nextStepDisabled]);

	const disableNext = () => {
		switch (index) {
			case TAB_INDEXES.LOCATION:
				return nextStepDisabled;
			case TAB_INDEXES.PLANS:
				if (!selectedOption.name) return true;
				else return false;
			case TAB_INDEXES.FORM:
				return !formik.isValid;
			default:
				return false;
		}
	};

	const handleBack = () => {
		if (index === TAB_INDEXES.PLANS) {
			routeToOrigin();
		}
		setIndex((prev) => prev - 1);
	};

	const handleNext = () => {
		//If it's form component then only goes inside this condition
		if (index === TAB_INDEXES.FORM) {
			formik.handleSubmit();
			if (formik.isValid) {
				TagManager.dataLayer({
					dataLayer: {
						event: 'Plan Select Step',
						step: 'contact details submit',
						language: getLanguage(),
					},
				});
				//If there are no inline errors then this condition is executed
				const values = { ...formik.values };
				const { contactNumber, plan, ...restValues } = values;
				const payload = {
					...restValues,
					productId: selectedOption.partNumber,
					productName: `${translate('mb.aamali.plus.plan', selectedOption.name)}`,
					phone: contactNumber,
					organizationName: props.account?.name,
					serviceList: [...additionalServices],
					crNumber: selectedAccount?.documentId, //Company Registration Number
					templateKey: templateKeys.aamaliPlusNew, // email template key
				};
				setLoading(true);
				SubmitSalesLeads.submitSalesLeads({
					...payload,
				}).subscribe(
					(response) => {
						setLoading(false);
						setSuccess(true);
						setRefNumber(response?.response?.data?.referenceNo);
						openModal();
						TagManager.dataLayer({
							dataLayer: {
								event: 'Plan Select Step',
								step: 'Request Submit Success',
								language: getLanguage(),
								referenceNum: response?.response?.data?.referenceNo,
								accountID: props.account?.id,
							},
						});
					},
					(error) => {
						setLoading(false);
						setSuccess(false);
						openModal();
						TagManager.dataLayer({
							dataLayer: {
								event: 'Plan Select Step',
								step: 'Request Submit Failure',
								language: getLanguage(),
								accountID: props.account?.id,
							},
						});
					}
				);
			} else return;
		} else {
			if (index === TAB_INDEXES.LOCATION) {
				TagManager.dataLayer({
					dataLayer: {
						event: 'Plan Select Step',
						step: 'Location selected',
						language: getLanguage(),
					},
				});
			}
			// This is the case for Additional benefits Page. mapping through productinfo and updating the state with the selected services
			// if (index === TAB_INDEXES.BENEFITS) {
			// 	const serviceData: any = [];
			// 	productInfo.forEach((product: any, index: any) => {
			// 		if (checkedState[index])
			// 			serviceData.push({ serviceProductId: product.partNumber, serviceProductName: product.name });
			// 	});
			// 	setAdditionalServices(serviceData);
			// }
			setIndex((prev) => prev + 1);
			if (selectedOption.name !== undefined)
				formik.setValues({ ...formik.values, plan: selectedOption.displayName });
		}
	};

	const renderNavButtons = (
		<NavButtons
			className={classes.navButtonWrapper}
			handleBack={handleBack}
			handleNext={handleNext}
			nextType={''}
			disabled={disableNext()}
			nextText={index === TAB_INDEXES.FORM ? translate('submit') : translate('next')}
		/>
	);

	const components = [
		{
			name: translate('add.new.mb.plansServices.tab'),
			content: (
				<>
					<MBPlans
						selectedOption={selectedOption}
						setSelectedOption={setSelectedOption}
						index={index}
						setIndex={setIndex}
					/>
					<Button color="tertiary-light" onClick={handleBack}>
						{translate('back')}
					</Button>
				</>
			),
		},
		// {
		// 	name: translate('add.new.mb.additionalBenefits.tab'),
		// 	content: (
		// 		<AdditionalBenefits
		// 			selectedOption={selectedOption}
		// 			checkedState={checkedState}
		// 			setCheckedState={setCheckedState}
		// 			productInfo={productInfo} // product additional services
		// 			setProductInfo={setProductInfo}
		// 			newCustomer={true} //will be true for add new microbundle flow and false for active services flow
		// 		/>
		// 	),
		// },
		{
			name: translate('add.new.mb.location.tab'),
			content: (
				<div ref={componentRef} tabIndex={0} className={classes.componentWrapper}>
					<Card className={classes.cardBody}>
						<LocationTab
							setNextStepDisabled={setNextStepDisabled}
							setGis={setGis}
							setAddress={setAddress}
							address={address}
							location={location}
							setLocation={setLocation}
							setLocationInputs={(name: string, value: string) => {
								setLocation((data) => ({ ...data, [name]: value }));
							}}
							mainContainerRef={componentRef}
						/>
						<div ref={navButtonsRef}>{renderNavButtons}</div>
					</Card>
				</div>
			),
		},
		{
			name: translate('add.new.mb.contact.details.tab'),
			content: (
				<div className={classes.componentWrapper}>
					<Card className={classes.cardBody}>
						<Form formik={formik} />
						{renderNavButtons}
					</Card>
				</div>
			),
		},
		{
			name: translate('add.new.mb.submit.tab'),
			content: <></>,
		},
	];

	const renderProgressBar = () => {
		return <TabsWithDots tabs={components} activeTab={index} setActiveTab={setIndex} />;
	};
	return (
		<>
			{loading ? (
				<CustomCircularProgress loading text="" transparent />
			) : (
				<>
					<div className={classes.addMBWrapper}>
						{renderProgressBar()}
						<StatusDialog
							isModalOpen={isModalOpen}
							closeModal={closeModal}
							heading={success ? translate('mb.thankyou.header') : translate('mb.failure.header')}
							desc={success ? translate('mb.thankyou.body', refNumber) : translate('mb.failure.body')}
							status={success ? SUCCESS : FAILURE}
							referenceNumber={refNumber}
							desc2={success ? translate('mb.thankyou.body2') : translate('mb.failure.body2')}
							footer={success ? translate('mb.thankyou.body3') : ''}
						/>
						{showNavButtons && renderNavButtons}
					</div>
				</>
			)}
		</>
	);
};

export default AddNewMb;
